/**
 * ConditionSummary renders a human-readable summary of a condition tree.
 */
export function ConditionSummary({ conditions }: { conditions: any }) {
  if (!conditions || (typeof conditions === 'object' && Object.keys(conditions).length === 0)) {
    return <span className="text-muted-foreground italic">(always)</span>
  }

  return <span className="text-sm">{renderCondition(conditions)}</span>
}

function renderCondition(cond: any): string {
  if (!cond || (typeof cond === 'object' && !Array.isArray(cond) && Object.keys(cond).length === 0)) {
    return '(always)'
  }

  if (cond.all && Array.isArray(cond.all)) {
    const parts = cond.all.map((c: any) => renderCondition(c))
    return parts.join(' AND ')
  }

  if (cond.any && Array.isArray(cond.any)) {
    const parts = cond.any.map((c: any) => renderCondition(c))
    return '(' + parts.join(' OR ') + ')'
  }

  if (cond.not) {
    return 'NOT ' + renderCondition(cond.not)
  }

  // Leaf condition
  const prop = cond.property || '?'
  const op = cond.op || '?'

  switch (op) {
    case 'is_empty':
      return `${prop} IS EMPTY`
    case 'is_set':
      return `${prop} IS SET`
    case 'eq':
      return `${prop} = ${formatValue(cond.value)}`
    case 'neq':
      return `${prop} != ${formatValue(cond.value)}`
    case 'gt':
      return `${prop} > ${formatValue(cond.value)}`
    case 'gte':
      return `${prop} >= ${formatValue(cond.value)}`
    case 'lt':
      return `${prop} < ${formatValue(cond.value)}`
    case 'lte':
      return `${prop} <= ${formatValue(cond.value)}`
    case 'in':
      return `${prop} IN [${(cond.values || []).map(formatValue).join(', ')}]`
    case 'intersects':
      return `${prop} INTERSECTS [${(cond.values || []).map(formatValue).join(', ')}]`
    default:
      return `${prop} ${op} ${formatValue(cond.value)}`
  }
}

function formatValue(v: any): string {
  if (v === true) return 'true'
  if (v === false) return 'false'
  if (v === null || v === undefined) return 'null'
  if (typeof v === 'string') return `"${v}"`
  return String(v)
}
